import { useEffect, useRef } from "react";

// Full-screen photo viewer: arrows + counter, swipe on phones, ← → / Esc on
// keyboards, tap outside or ✕ to close, background scroll locked while open.
// photos: [{ url, caption? }] · index: number | null (null = closed)
export default function PhotoLightbox({ photos, index, onIndex, onClose }) {
  const touch = useRef(null);
  const open = index != null && photos && photos[index];
  const n = photos?.length || 0;
  const go = (d) => onIndex(((index ?? 0) + d + n) % n);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" && n > 1) go(1);
      else if (e.key === "ArrowLeft" && n > 1) go(-1);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prevOverflow; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index, n]);

  if (!open) return null;
  const ph = photos[index];
  const arrow = (side) => ({
    position: "absolute", top: "50%", [side]: 10, transform: "translateY(-50%)", width: 44, height: 44, borderRadius: "50%",
    border: "none", background: "rgba(255,255,255,0.16)", color: "#fff", fontSize: 26, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
  });
  return (
    <div role="dialog" aria-modal="true" aria-label="相片"
      onClick={onClose}
      onTouchStart={(e) => { touch.current = e.touches.length === 1 ? e.touches[0].clientX : null; }}
      onTouchEnd={(e) => {
        if (touch.current == null || n < 2) return;
        const dx = e.changedTouches[0].clientX - touch.current;
        touch.current = null;
        if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
      }}
      style={{ position: "fixed", inset: 0, zIndex: 400, background: "rgba(0,0,0,0.92)", display: "flex",
        flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "56px 12px 24px" }}>
      <button type="button" aria-label="關閉" onClick={(e) => { e.stopPropagation(); onClose(); }}
        style={{ position: "absolute", top: "calc(12px + env(safe-area-inset-top))", right: 12, width: 40, height: 40, borderRadius: "50%",
          border: "none", background: "rgba(255,255,255,0.16)", color: "#fff", fontSize: 22, cursor: "pointer" }}>✕</button>
      {n > 1 && (
        <div style={{ position: "absolute", top: "calc(20px + env(safe-area-inset-top))", left: 0, right: 0, textAlign: "center",
          color: "rgba(255,255,255,0.85)", fontSize: 13, fontWeight: 800, pointerEvents: "none" }}>{index + 1} / {n}</div>
      )}
      <img src={ph.url} alt={ph.caption || ""} onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "100%", maxHeight: "calc(100% - 40px)", objectFit: "contain", borderRadius: 8, touchAction: "pinch-zoom", userSelect: "none" }} />
      {ph.caption && (
        <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 12, color: "#fff", fontSize: 14, fontWeight: 700, textAlign: "center", opacity: 0.9 }}>{ph.caption}</div>
      )}
      {n > 1 && (
        <>
          <button type="button" aria-label="上一張" onClick={(e) => { e.stopPropagation(); go(-1); }} style={arrow("left")}>‹</button>
          <button type="button" aria-label="下一張" onClick={(e) => { e.stopPropagation(); go(1); }} style={arrow("right")}>›</button>
        </>
      )}
    </div>
  );
}
